<template>
  <div
    ref="root"
    class="navigation-menu"
    @keydown.capture="wrapArrowKeyAround($event); selectFocusedEntryOnEnter($event)"
  >
    <PrmMenu
      ref="menu"
      :model="menuModel"
      @focus="focusActiveEntry()"
    >
      <template
        #submenuheader="{
          item,
        }"
      >
        <slot
          name="group-title"
          :label="item.navigationMenuGroup.label"
          :count="item.navigationMenuGroup.count"
        >
          <span class="navigation-menu__group-title flex-row justify-spaced typo-h5">
            <span>{{ item.navigationMenuGroup.label }}</span>
            <span v-if="item.navigationMenuGroup.count !== undefined">{{ item.navigationMenuGroup.count }}</span>
          </span>
        </slot>
      </template>
      <template
        #item="{
          item,
        }"
      >
        <component
          :is="item.href ? 'a' : 'span'"
          :href="item.href"
          class="navigation-menu__entry flex-row align-center justify-spaced cursor-pointer"
          :class="{
            'navigation-menu__entry_active': props.activeKey === item.key,
          }"
          :aria-current="props.activeKey === item.key ? props.ariaCurrentValue : undefined"
          data-qa="navigation-menu__entry"
          :data-qa-id="item.key"
          @click="preventPlainLinkClick($event)"
        >
          <span class="navigation-menu__title flex-row align-center">
            <i
              v-if="typeof item.entryIcon === 'string'"
              :class="item.entryIcon"
              class="navigation-menu__icon"
            />
            <component
              :is="item.entryIcon"
              v-else-if="item.entryIcon"
              class="navigation-menu__icon"
            />
            <span class="navigation-menu__label">{{ item.label }}</span>
          </span>
          <PrmTag
            v-if="item.tag"
            class="navigation-menu__tag"
          >
            <!-- the font of the tag itself outranks the utility classes -->
            <span class="typo-small text-bold">{{ item.tag }}</span>
          </PrmTag>
          <span
            v-else-if="item.count !== undefined"
            class="navigation-menu__count typo-hint color-text-secondary"
          >{{ item.count }}</span>
          <span
            v-else-if="item.detail"
            class="navigation-menu__detail typo-hint color-text-secondary"
          >{{ item.detail }}</span>
        </component>
      </template>
    </PrmMenu>
  </div>
</template>

<script setup lang="ts">
import PrmMenu from '@NitrexGroup/primevue/menu';
import type {
  MenuItem,
} from '@NitrexGroup/primevue/menuitem';
import PrmTag from '@NitrexGroup/primevue/tag';
import {
  computed,
  useTemplateRef,
} from 'vue';

import {
  EKeyboardKey,
} from '../../constants/keyboard.ts';

import {
  INavigationMenuEntry,
  TNavigationMenuItem,
  isNavigationMenuGroup,
} from './navigation-menu.ts';

const props = withDefaults(defineProps<{
  items: TNavigationMenuItem[],
  activeKey?: string,
  /** how the active entry is announced, e.g. `page` for the pages of an app, `location` for anchors of a page */
  ariaCurrentValue?: 'page' | 'location' | 'true',
}>(), {
  activeKey: undefined,
  ariaCurrentValue: 'true',
});

defineSlots<{
  'group-title'?(props: {
    label: string,
    count?: number,
  }): any,
}>();

const emit = defineEmits<{
  (e: 'select', key: string): void,
}>();

/**
 * The select event decides what a click on a link does; a click with a modifier key still opens the link in a new
 * tab or window.
 */
function preventPlainLinkClick(event: MouseEvent) {
  const hasModifierKey = event.ctrlKey || event.metaKey || event.shiftKey || event.altKey;
  if (event.currentTarget instanceof HTMLAnchorElement && !hasModifierKey) {
    event.preventDefault();
  }
}

/** The PrimeVue Menu moves its focus with this method, but does not declare it in its types. */
interface IMenuFocus {
  changeFocusedOptionIndex(index: number): void,
}

const rootElement = useTemplateRef<HTMLDivElement>('root');
const menu = useTemplateRef<InstanceType<typeof PrmMenu>>('menu');

function findEntryElementList(): HTMLElement[] {
  return Array.from(rootElement.value?.querySelectorAll<HTMLElement>('li[data-pc-section="menuitem"]') ?? []);
}

function focusEntry(entryIndex: number) {
  (menu.value as unknown as IMenuFocus | null)?.changeFocusedOptionIndex(entryIndex);
}

/** The Menu focuses its first entry when it receives the focus, e.g. on Alt/⌥ + 2. */
function focusActiveEntry() {
  const activeEntryIndex = findEntryElementList()
    .findIndex((entryElement) => entryElement.querySelector('.navigation-menu__entry_active'));
  if (activeEntryIndex > -1) {
    focusEntry(activeEntryIndex);
  }
}

/** The Menu stops at its first and last entry; the arrow keys continue at the other end instead. */
function wrapArrowKeyAround(event: KeyboardEvent) {
  const isArrowDown = event.key === EKeyboardKey.ArrowDown;
  const isArrowUp = event.key === EKeyboardKey.ArrowUp;
  if (!(isArrowDown || isArrowUp) || !(event.target instanceof HTMLElement)) {
    return;
  }
  const entryElementList = findEntryElementList();
  const focusedEntryId = event.target.getAttribute('aria-activedescendant');
  const focusedEntryIndex = entryElementList.findIndex((entryElement) => entryElement.id === focusedEntryId);
  const lastEntryIndex = entryElementList.length - 1;
  if (isArrowDown && focusedEntryIndex === lastEntryIndex) {
    focusEntry(0);
  } else if (isArrowUp && focusedEntryIndex === 0) {
    focusEntry(lastEntryIndex);
  } else {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
}

/**
 * The Menu answers Enter and Space with a click on the entry, which misses the click handler of its content, as the
 * entries are drawn by the item slot.
 */
function selectFocusedEntryOnEnter(event: KeyboardEvent) {
  const isSelectKey = event.key === EKeyboardKey.Enter || event.key === EKeyboardKey.Space;
  if (!isSelectKey || !(event.target instanceof HTMLElement)) {
    return;
  }
  const focusedEntryId = event.target.getAttribute('aria-activedescendant');
  const focusedEntryElement = findEntryElementList().find((entryElement) => entryElement.id === focusedEntryId);
  const focusedEntryContent = focusedEntryElement?.querySelector<HTMLElement>('[data-pc-section="content"]');
  if (!focusedEntryContent) {
    return;
  }
  focusedEntryContent.click();
  event.preventDefault();
  event.stopPropagation();
}

/** The icon of an entry may be a component, which the `icon` of a PrimeVue MenuItem does not take. */
function createEntryMenuItem(navigationMenuEntry: INavigationMenuEntry): MenuItem {
  const {
    icon: entryIcon,
    ...menuItemFields
  } = navigationMenuEntry;
  return {
    ...menuItemFields,
    entryIcon,
    command: () => emit('select', navigationMenuEntry.key),
  };
}

const menuModel = computed<MenuItem[]>(() => props.items.map((navigationMenuItem) => {
  if (isNavigationMenuGroup(navigationMenuItem)) {
    return {
      label: navigationMenuItem.label,
      count: navigationMenuItem.count,
      navigationMenuGroup: navigationMenuItem,
      items: navigationMenuItem.entries.map(createEntryMenuItem),
    };
  }
  return createEntryMenuItem(navigationMenuItem);
}));
</script>

<style lang="scss" scoped>
// The menu is spaced like a tree. A denser list, e.g. of the anchors of a page, sets --navigation-menu-entry-padding,
// --navigation-menu-entry-spacing, --navigation-menu-icon-gap and --navigation-menu-group-title-margin on it.
// The Menu reads its colors and spacing from these custom properties of the PrimeVue theme. It renders through a
// portal, so the scoped attribute of this component only reaches it through :deep().
.navigation-menu :deep(.p-menu) {
  --menu-bg: transparent;
  --menu-border: 0 none;
  --menu-width: 0;
  --vertical-menu-padding: 0;
  --input-list-item-margin: 0 0 var(--navigation-menu-entry-spacing, calc(2 * var(--tree-node-padding)));
  --menuitem-text-color: var(--text-color);
  --menuitem-text-hover-color: var(--text-color);
  --menuitem-hover-bg: transparent;
  --submenu-header-margin: var(--navigation-menu-group-title-margin, var(--panel-content-padding) 0);
  --submenu-header-padding: 0;
  --submenu-header-bg: transparent;

  width: 100%;

  .p-submenu-header:first-child {
    margin-top: 0;
  }

  .p-menuitem-content:hover .navigation-menu__entry,
  .p-focus > .p-menuitem-content .navigation-menu__entry {
    background: var(--surface-hover);
  }

  .p-menuitem-content .navigation-menu__entry_active {
    background: var(--primary-color-subtle);
    color: var(--primary-color);
  }

  // The hover color alone is too faint to follow the arrow keys, and the active entry has none.
  .p-menu-list:focus-visible .p-focus .navigation-menu__entry {
    box-shadow: inset 0 0 0 1px var(--focus-outline-color);
  }
}

.navigation-menu__title {
  gap: var(--navigation-menu-icon-gap, var(--inline-spacing));
  min-width: 0;
}

.navigation-menu__icon {
  flex: none;
  width: 1.375rem;
  height: 1.375rem;
  font-size: 1.125rem;
  text-align: center;
}

.navigation-menu__entry {
  padding: var(--navigation-menu-entry-padding, var(--tree-node-content-padding));
  border-radius: var(--border-radius);
  color: inherit;
  text-decoration: none;
}

.navigation-menu__label {
  min-width: 0;
  overflow-wrap: anywhere;
}

.navigation-menu__count {
  font-family: var(--font-family-monospaced);
}

.navigation-menu__detail {
  flex: none;
  white-space: nowrap;
}

.navigation-menu__tag {
  padding: 0 var(--gap-union);
  border: 1px solid currentcolor;
  border-radius: var(--border-radius-small);
  background: transparent;
  color: var(--primary-color);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
</style>
